import { isPublicPath } from './gate';

/**
 * What to do when a session that was believed valid turns out not to be.
 *
 * The router guard in `__root.tsx` only re-checks `auth.me` on navigation, and that check is
 * cached for 60s (see `sessionQueryOptions`) so switching pages right after an idle timeout
 * does not itself trip the guard. The page loads, its own queries fire, and the server — the
 * actual authority — refuses them. Without this, that refusal just left the page half-drawn
 * with no data and no explanation, which is the bug this file exists to close: whoever fetches
 * data with an expired session tells the router, and the router sends the user to `/login`
 * instead of leaving them staring at an empty screen.
 *
 * Registered from `main.tsx` once the router exists. `lib/trpc.ts` needs to call this from
 * inside `QueryClient` construction, which happens before the router does — going through a
 * setter here avoids a cycle where the query client depends on the router and the router
 * depends on the query client.
 */
let redirectToLogin: ((returnTo: string) => void) | null = null;

export function registerAuthExpiryHandler(handler: (returnTo: string) => void): void {
  redirectToLogin = handler;
}

// Several queries on the same page fail together — one redirect, not one per query.
let redirecting = false;

/**
 * `currentPath` is the caller's job to read (`window.location...`), not this module's — it
 * keeps the dedupe-and-exempt-public-paths logic here testable as plain data in, call out,
 * with no DOM required.
 */
export function handleAuthExpiry(currentPath: string): void {
  if (redirecting) return;

  // A public page (login itself, signup, ...) has nothing authenticated to fail in the first
  // place; treating a stray error there as an expiry would just bounce it to itself.
  const pathname = currentPath.split(/[?#]/)[0] ?? currentPath;
  if (isPublicPath(pathname)) return;

  redirecting = true;
  redirectToLogin?.(currentPath);
}

/** Cleared once a session exists again, alongside the rest of the previous identity's cache. */
export function resetAuthExpiryGuard(): void {
  redirecting = false;
}
